import type { CostChange, CostChangeCause, CostChangeFilter, CostChangeKind, CostChangeStatus, CostContract, CostLineInput, CostLineView, CostRow } from '../types'
import { compareMoneyStrings, sumMoneyStrings, type DraftLine } from './contracts'
import { CHANGE_STATUS_KEY } from './labels'
import type { ModuleActions } from './permissions'

// ---- Register filter ----

export interface ChangeFilterState { kind: CostChangeKind | null; status: CostChangeStatus | null; contractId: string | null; search: string }
export const EMPTY_CHANGE_FILTER: ChangeFilterState = { kind: null, status: null, contractId: null, search: '' }

/** The API filter: only the fields that are set; `search` is the debounced text. */
export function toChangeFilter(s: ChangeFilterState, search: string): CostChangeFilter {
  const f: CostChangeFilter = {}
  if (s.kind) f.kind = s.kind
  if (s.status) f.status = s.status
  if (s.contractId) f.contractId = s.contractId
  if (search.trim()) f.search = search.trim()
  return f
}
export const isChangeFiltered = (s: ChangeFilterState): boolean => !!(s.kind || s.status || s.contractId || s.search.trim())

// ---- Rights (spec §1.4, §7.1) ----

export interface ChangeAffordances {
  create: boolean
  editHeader: boolean
  editLines: boolean
  submit: boolean
  approve: boolean
  reject: boolean
  reopen: boolean
  unapprove: boolean
  remove: boolean
  addAttachment: boolean
  removeAttachment: boolean
  convert: boolean
  createRfq: boolean
  dismiss: boolean
}

/** The server enforces the same rules; this only hides what it would refuse. `c` is null on the register. */
export function changeAffordances(a: ModuleActions, c: (Pick<CostChange, 'status'> & Partial<Pick<CostChange, 'kind' | 'takenOver' | 'sourcedRfqId'>>) | null): ChangeAffordances {
  const draft = c?.status === 'DRAFT'
  const pending = c?.status === 'PENDING'
  const potential = c?.kind === 'POTENTIAL'
  // Potential changes spec §2: once its RFQ took it over, a potential change is read-only (deletion aside).
  const locked = potential && !!c?.takenOver
  const open = draft && !locked
  // Spec §4: an RFQ is the chosen path, so a potential change with one is no longer converted or sent to another RFQ.
  const choosable = potential && open && !c?.sourcedRfqId
  return {
    create: a.canCreate,
    editHeader: a.canEdit && open,
    editLines: a.canEdit && open,
    submit: a.canEdit && draft && !potential,
    approve: a.canApprove && pending,
    reject: a.canApprove && pending,
    reopen: a.canEdit && c?.status === 'REJECTED',
    unapprove: a.canApprove && c?.status === 'APPROVED',
    remove: a.canDelete && draft,
    addAttachment: a.canCreate && !!c && !locked,
    removeAttachment: a.canDelete && !!c && !locked,
    convert: a.canEdit && choosable,
    createRfq: a.canCreate && choosable,
    dismiss: a.canEdit && potential && open,
  }
}

/** Why the server would refuse "Odeslat ke schválení" (spec §1.4, ruling Q5), as an i18n key, or null. */
export function changeSubmitBlocker(c: Pick<CostChange, 'status' | 'kind' | 'lineCount' | 'total'>): string | null {
  if (c.status !== 'DRAFT') return null
  if (c.lineCount === 0) return 'CostControl_change_submit_needs_lines'
  if (c.kind === 'TRANSFER' && compareMoneyStrings(c.total, '0') !== 0) return 'CostControl_change_submit_unbalanced'
  return null
}

/** Potential changes spec §2: a potential change reads "Otevřená" / "Nenastala"; `tone` picks the badge variant. */
export function changeStatusView(c: Pick<CostChange, 'kind' | 'status'>): { key: string; tone: string } {
  if (c.kind === 'POTENTIAL' && c.status === 'DRAFT') return { key: 'CostControl_change_status_POTENTIAL_OPEN', tone: 'DRAFT' }
  if (c.kind === 'POTENTIAL' && c.status === 'REJECTED') return { key: 'CostControl_change_status_POTENTIAL_DISMISSED', tone: 'DISMISSED' }
  return { key: CHANGE_STATUS_KEY[c.status], tone: c.status }
}

// ---- Header form ----

export interface ChangeForm { kind: CostChangeKind; title: string; reason: string; contractId: string | null; cause?: CostChangeCause | null }
export const emptyChangeForm = (kind: CostChangeKind = 'BUDGET_CHANGE', contractId: string | null = null): ChangeForm => ({ kind, title: '', reason: '', contractId, cause: null })
export const formFromChange = (c: CostChange): ChangeForm => ({ kind: c.kind, title: c.title, reason: c.reason ?? '', contractId: c.contractId, cause: c.cause })

/** The first problem as an i18n key (the server checks the same), or null. */
export function changeFormProblem(f: ChangeForm): string | null {
  if (!f.title.trim()) return 'CostControl_error_title_required'
  if (f.kind === 'CONTRACT_CHANGE' && !f.contractId) return 'CostControl_error_change_contract_required'
  return null
}

/**
 * Form → API input: `kind` only on create; a transfer never names a contract; empty text → null.
 *
 * In edit mode `contractId` is sent only when it actually changed (M6): the server re-validates a named contract
 * (`assertChangeContract`) whenever the field is present at all, including "not active" for a subcontract that
 * closed after the change was drafted. Resending the stored value on every save — e.g. to fix a typo in the title —
 * would then be refused for a contract nobody touched. `original` is the change being edited; create mode always
 * sends `contractId` as before.
 */
export function changeInput(f: ChangeForm, mode: 'create' | 'edit', original?: (Pick<CostChange, 'contractId'> & Partial<Pick<CostChange, 'cause'>>) | null): Record<string, unknown> {
  const contractId = f.kind === 'TRANSFER' ? null : f.contractId
  const input: Record<string, unknown> = {
    ...(mode === 'create' ? { kind: f.kind } : {}),
    title: f.title.trim(),
    reason: f.reason.trim() || null,
  }
  if (mode === 'create' || !original || contractId !== original.contractId) input.contractId = contractId
  // The cause is optional on every kind (potential changes spec §2): sent on create when chosen, on edit when it changed.
  const cause = f.cause ?? null
  if (mode === 'create' ? cause !== null : cause !== (original?.cause ?? null)) input.cause = cause
  return input
}

/**
 * Contracts a change of `kind` may name (spec §1.4): ACTIVE subcontracts, the main contract, any open contract for a potential change, or none. In edit mode
 * the change's own `currentContractId` is always offered too, even once it stopped being ACTIVE (M6) — otherwise the
 * Select would show blank for a change whose contract has since closed.
 */
export function changeContractOptions<C extends Pick<CostContract, 'id' | 'kind' | 'status'>>(contracts: C[], kind: CostChangeKind, currentContractId?: string | null): C[] {
  // Potential changes spec §2: any open contract pre-fills a later conversion.
  if (kind === 'POTENTIAL') return contracts.filter((c) => c.kind === 'MAIN' || c.status !== 'CLOSED' || c.id === currentContractId)
  if (kind === 'CONTRACT_CHANGE') return contracts.filter((c) => c.kind === 'SUB' && (c.status === 'ACTIVE' || c.id === currentContractId))
  if (kind === 'BUDGET_CHANGE') return contracts.filter((c) => c.kind === 'MAIN')
  return []
}

/**
 * Items of a change whose forecast follows their budget (no subcontract line, expense or manual forecast): spec §1.3 counts a
 * pending contract change there only once approved, so converting a potential change to one drops it from the forecast meanwhile.
 */
export function itemsOutsideCostForecast(
  lines: Pick<CostLineView, 'nodeId' | 'nodeName'>[], rows: Pick<CostRow, 'id' | 'costBasedForecast' | 'hasForecastOverride'>[],
): string[] {
  const byId = new Map(rows.map((r) => [r.id, r]))
  const names = lines.filter((l) => { const r = byId.get(l.nodeId); return !!r && !r.costBasedForecast && !r.hasForecastOverride }).map((l) => l.nodeName)
  return names.filter((n, i) => names.indexOf(n) === i)
}

// ---- Lines editor ----

export interface ChangeDraftCheck { total: string; problem: string | null; balanced: boolean }

/**
 * Running total of the valid signed amounts, the first problem (an i18n key) and, for a transfer, whether it adds up
 * to zero. An unbalanced transfer can be saved; only submitting needs the balance (ruling Q5).
 */
/** A transfer's lines must sum to zero, so a positive amount split pro rata only fits budget and contract changes. */
export const canAllocateChange = (kind: CostChangeKind): boolean => kind !== 'TRANSFER'

export function checkChangeDraftLines(lines: DraftLine[], kind: CostChangeKind): ChangeDraftCheck {
  const total = sumMoneyStrings(lines.filter((l) => l.valid && l.amount !== null).map((l) => l.amount as string))
  const nodeIds = lines.map((l) => l.nodeId)
  let problem: string | null = null
  if (lines.some((l) => !l.valid)) problem = 'CostControl_lines_problem_invalid'
  else if (lines.some((l) => l.amount === null || compareMoneyStrings(l.amount, '0') === 0)) problem = 'CostControl_lines_problem_nonzero'
  else if (new Set(nodeIds).size !== nodeIds.length) problem = 'CostControl_lines_problem_duplicate_item'
  return { total, problem, balanced: kind !== 'TRANSFER' || compareMoneyStrings(total, '0') === 0 }
}

export const toChangeLineInputs = (lines: DraftLine[]): CostLineInput[] => lines.map((l) => ({ nodeId: l.nodeId, amount: l.amount as string }))
